import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Upload, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LEVELS, COMPETENCES, LEVEL_STYLES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, SectionHeader, PrimaryBtn, IconBtn, Field } from "./ui";

export default function WorkshopsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/workshops").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const empty = { title: "", level: "3e", competence: COMPETENCES[0], summary: "", description: "", duration: "2h00", price: 180, image: "", materials: "", objectives: "" };
  const [form, setForm] = useState(empty);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (w) => {
    setEditing(w);
    setForm({ ...w, price: w.price, materials: (w.materials || []).join(", "), objectives: (w.objectives || []).join(", ") });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title, level: form.level, competence: form.competence, summary: form.summary,
      description: form.description, duration: form.duration, price: parseFloat(form.price) || 0, image: form.image,
      materials: form.materials.split(",").map((s) => s.trim()).filter(Boolean),
      objectives: form.objectives.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/workshops/${editing.id}`, payload);
      else await api.post("/workshops", payload);
      toast.success(editing ? "Atelier mis a jour" : "Atelier cree");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (w) => {
    if (!window.confirm(`Supprimer "${w.title}" ?`)) return;
    await api.delete(`/workshops/${w.id}`);
    toast.success("Atelier supprimé");
    load();
  };

  const uploadFiche = async (w, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/workshops/${w.id}/fiche`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fiche PDF téléversée");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Card>
      <SectionHeader title="Ateliers" action={<PrimaryBtn onClick={openNew} data-testid="admin-new-workshop"><Plus className="w-4 h-4" /> Nouvel atelier</PrimaryBtn>} />
      <div className="space-y-3" data-testid="admin-workshops-list">
        {items.map((w) => {
          const s = LEVEL_STYLES[w.level] || LEVEL_STYLES["3e"];
          return (
            <div key={w.id} className="flex items-center gap-4 border border-slate-100 rounded-2xl p-4">
              <img src={w.image} alt="" className="w-14 h-14 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900 truncate">{w.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
                  <span className="text-xs text-slate-400">{w.competence}</span>
                  {w.fiche_path && <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold"><FileCheck2 className="w-3.5 h-3.5" /> PDF</span>}
                </div>
              </div>
              <label className="grid place-items-center w-9 h-9 rounded-full text-amber-600 hover:bg-amber-50 cursor-pointer" title="Televerser une fiche PDF" data-testid={`admin-upload-fiche-${w.id}`}>
                <Upload className="w-4 h-4" />
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadFiche(w, e.target.files[0])} />
              </label>
              <IconBtn icon={Pencil} color="sky" onClick={() => openEdit(w)} testid={`admin-edit-workshop-${w.id}`} />
              <IconBtn icon={Trash2} color="rose" onClick={() => remove(w)} testid={`admin-delete-workshop-${w.id}`} />
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Modifier l'atelier" : "Nouvel atelier"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="admin-workshop-form">
            <Field label="Titre"><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1" data-testid="wf-title" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Niveau">
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="wf-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{LEVEL_STYLES[l].label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Compétence">
                <Select value={form.competence} onValueChange={(v) => setForm({ ...form, competence: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="wf-competence"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMPETENCES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Résumé"><Textarea required rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="rounded-xl mt-1" data-testid="wf-summary" /></Field>
            <Field label="Description"><Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" data-testid="wf-description" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Durée"><Input required value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="rounded-xl mt-1" data-testid="wf-duration" /></Field>
              <Field label="Tarif (EUR)"><Input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-xl mt-1" data-testid="wf-price" /></Field>
            </div>
            <Field label="Image (URL)"><Input required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-xl mt-1" data-testid="wf-image" /></Field>
            <Field label="Matériel (séparé par des virgules)"><Input value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} className="rounded-xl mt-1" data-testid="wf-materials" /></Field>
            <Field label="Objectifs (séparés par des virgules)"><Input value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} className="rounded-xl mt-1" data-testid="wf-objectives" /></Field>
            <PrimaryBtn type="submit" data-testid="wf-submit">{editing ? "Enregistrer" : "Créer"}</PrimaryBtn>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
